import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface RankedItem {
  id: string;
  label: string;
  /** What sits in the row beside the label - the number or address this channel goes to. */
  field?: ReactNode;
  /** Why this row cannot be switched on yet ("Enter the WhatsApp number first"). */
  blockedReason?: string;
}

/**
 * A ranked choice (owner, 2026-09-27: "list type viz. mobile, email with fields and toggle
 * buttons ... in flow"). Every option is a row; the rows that are switched on come first, in the
 * order they will be tried, and only those carry a rank and can be moved. Switching a row on puts
 * it last among the chosen; switching it off drops it below them.
 *
 * `value` is the ranked list of switched-on ids, which is exactly what is stored.
 */
export function RankedList({ items, value, onChange, label, emptyNote, pinned = {} }: {
  items: RankedItem[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  /** Rows fixed on and in place, with why ("English is on every farmer's list"). */
  pinned?: Record<string, string>;
  /** Shown when nothing is switched on, so "none" reads as a decision rather than a blank. */
  emptyNote?: string;
}) {
  const chosen = value.map((id) => items.find((item) => item.id === id)).filter((item): item is RankedItem => Boolean(item));
  const rest = items.filter((item) => !value.includes(item.id));

  function move(index: number, by: -1 | 1) {
    const next = [...value];
    const here = next[index];
    const there = next[index + by];
    if (here === undefined || there === undefined) return;
    next[index] = there;
    next[index + by] = here;
    onChange(next);
  }

  return (
    <div aria-label={label} className="ranked-list" role="list">
      {[...chosen, ...rest].map((item) => {
        const rank = value.indexOf(item.id);
        const on = rank >= 0;
        const blocked = !on && Boolean(item.blockedReason);
        const fixed = pinned[item.id];
        const neighbourFixed = (by: -1 | 1) => Boolean(pinned[value[rank + by] ?? '']);
        return (
          <div className={`ranked-row${on ? ' is-on' : ''}`} key={item.id} role="listitem">
            <span aria-label={on ? `Choice ${rank + 1}` : 'Not used'} className="ranked-rank">{on ? rank + 1 : '-'}</span>
            <span className="ranked-label">{item.label}</span>
            <div className="ranked-field">{item.field}</div>
            <div className="ranked-actions">
              <button aria-label={`Move ${item.label} up`} className="icon-only" disabled={!on || rank === 0 || Boolean(fixed) || neighbourFixed(-1)}
                      onClick={() => move(rank, -1)} type="button"><ChevronUp size={15} /></button>
              <button aria-label={`Move ${item.label} down`} className="icon-only" disabled={!on || rank === value.length - 1 || Boolean(fixed) || neighbourFixed(1)}
                      onClick={() => move(rank, 1)} type="button"><ChevronDown size={15} /></button>
              <button aria-pressed={on} className="onoff-toggle" disabled={blocked || (on && Boolean(fixed))}
                      onClick={() => onChange(on ? value.filter((id) => id !== item.id) : [...value, item.id])}
                      title={blocked ? item.blockedReason : on && fixed ? fixed : undefined} type="button">
                {on ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        );
      })}
      {value.length === 0 && emptyNote && <p className="muted ranked-empty">{emptyNote}</p>}
    </div>
  );
}
